<script lang="ts" setup>
import { imgProps } from '#image/components/_base';
import { useColorMode, useImage } from '#imports';

type ImageType = 'big' | 'default';
type ColorMode = 'dark-mode' | 'light-mode';
type PlaceholderType = 'skeleton' | 'missing' | 'missingBig';
type Placeholders = Record<ColorMode, Record<PlaceholderType, string>>;

defineOptions({
    inheritAttrs: false
});

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    type: {
        type: String as () => ImageType,
        default: 'default'
    },
    ...imgProps,
    width: {
        type: Number,
        required: false
    },
    height: {
        type: Number,
        required: false
    }
});

const isError = ref(false);
const isLoaded = ref(false);
const isLoading = ref(true);

const img = useImage();
const attrs = useAttrs();
const colorMode = useColorMode();

const placeholders: Placeholders = {
    'dark-mode': {
        skeleton: img('/skeleton-dark.svg'),
        missing: img('/missing-dark.svg'),
        missingBig: img('/missing-small-dark.svg')
    },
    'light-mode': {
        skeleton: img('/skeleton-light.svg'),
        missing: img('/missing-light.svg'),
        missingBig: img('/missing-small-light.svg')
    }
};

const placeholder = computed(() => {
    const mode = colorMode.value as ColorMode;

    if (isError.value) {
        return props.type === 'big'
            ? placeholders[mode].missingBig
            : placeholders[mode].missing;
    }

    return placeholders[mode].skeleton;
});

function handleError() {
    isLoading.value = false;
    isError.value = true;
}

function handleLoad() {
    isLoaded.value = true;
    isLoading.value = false;
    isError.value = false;
}

function calcSizes({ width, height }: { width?: number; height?: number }) {
    return {
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined
    };
}

function cleanAttrs(attrs: Record<string, unknown>) {
    const { srcset, sizes, width, height, ...cleanedAttrs } = attrs;
    return cleanedAttrs;
}

watch(() => props.src, (newSrc, oldSrc) => {
    if (newSrc && !oldSrc) {
        handleLoad();
    }

    if (!newSrc || newSrc.trim() === '') {
        handleError();
    }
});

onMounted(() => {
    if (!props.src || props.src.trim() === '') {
        handleError();
    }
});
</script>

<template>
  <NuxtImg v-bind="{ ...attrs, ...props }"
           :custom="true"
           :key="props.src"
           :alt="props.alt"
           :width="props.width"
           :height="props.height"
           :style="calcSizes({ width: props.width, height: props.height })"
           v-slot="{ src, isLoaded: slotLoaded, imgAttrs: slotAttrs }"
  >
    <img v-if="slotLoaded && !isError"
         v-bind="slotAttrs"
         :src="src"
         :alt="props.alt"
         draggable="false"
         :class="props.class"
    >

    <img v-else-if="isLoading"
         :src="placeholder"
         v-bind="cleanAttrs(slotAttrs)"
         draggable="false"
         :style="calcSizes({ width: slotAttrs.width, height: slotAttrs.height })"
         alt="Skeleton"
    >
    <img v-else
         :src="placeholder"
         v-bind="cleanAttrs(slotAttrs)"
         draggable="false"
         :style="[calcSizes({ width: slotAttrs.width, height: slotAttrs.height }), {objectFit: 'contain'}]"
         alt="Error Placeholder"
         :class="props.class"
    >
  </NuxtImg>
</template>
